/**
 * «Confirmación de entrega» — what drivers confirmed from the DAC app (#160, design D8).
 *
 * Each confirmation is for one consignment, named **as it was when the confirmation arrived**: the
 * confirmation's own copy, never the document's current consignment of that number, because an
 * in-place amendment rewrites the document's consignments and the panel must say what was
 * confirmed. «Fotografiada», or «Confirmada» for a confirmation with no photograph (#179), is the
 * phone's clock, kept as the phone sent it, and is labelled so; «Recibida» is the server's.
 *
 * The photographs and the driver's data are the company administrators' (Addendum No. 3, Clause
 * 1.3(c)): a photograph shows the names, signatures and stamps of the people who received the
 * goods, and the NIF and the plate are as the phone sent them: the ones its lookup found the
 * document with, or the document's own when the phone created it (#110). ASG Mobile sends both in
 * the lookup's canonical form, upper case with letters and digits only (§29 of the architecture
 * spec), so a plate the document holds as `1234 BCD` is shown as `1234BCD`. The server sends those
 * fields and serves the photographs to an administrator only. This panel does not lean on that
 * alone: for any other role it asks for no photograph and prints none of the fields, whatever a
 * response carries.
 *
 * Before the first confirmation `delivery` is `null`, and the panel says so without asking the
 * server anything.
 */

import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { DeliveryConfirmation, DeliverySummary } from '@deca/api'

import { saveBlob } from '../app/api.ts'
import { useDeliveryConfirmations, useDeliveryPhoto } from '../app/queries.ts'
import { hasDeliveryConfirmation } from '../deca/delivery.ts'
import { formatDateTime } from '../deca/format.ts'
import { Dialog } from './Dialog.tsx'
import { Icon } from './Icon.tsx'
import { Button, Datum, ErrorState, LoadingRows, Notice } from './primitives.tsx'
import { useObjectUrl } from './useObjectUrl.ts'

/**
 * The photographs' numbers, 1 to `photoCount`. Capped at the contract's three: a count no server
 * sends must not turn into a burst of requests, each of them an audit row.
 */
function photoNumbers(confirmation: DeliveryConfirmation): number[] {
  return Array.from({ length: Math.min(Math.max(confirmation.photoCount, 0), 3) }, (_, i) => i + 1)
}

export function DeliveryPanel({
  deca,
  administer,
}: {
  /** `delivery` optional: absent reads as `null` (see `deca/delivery.ts`). */
  deca: { id: string; documentNumber?: string | null; delivery?: DeliverySummary | null }
  /** `abilities.administer`: ORG_ADMIN, and SUPER_ADMIN inside the company. */
  administer: boolean
}) {
  const { t, i18n } = useTranslation()
  const confirmed = hasDeliveryConfirmation(deca)
  const query = useDeliveryConfirmations(deca.id, confirmed)
  const [open, setOpen] = useState<{ confirmation: DeliveryConfirmation; n: number } | null>(null)

  if (!confirmed) return <p className="small muted">{t('delivery.none')}</p>
  if (query.isLoading) return <LoadingRows rows={2} columns={2} />
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const confirmations = query.data?.data ?? []
  if (confirmations.length === 0) return <p className="small muted">{t('delivery.none')}</p>

  return (
    <>
      <ol className="delivery-list">
        {confirmations.map((confirmation) => (
          <li key={confirmation.id} className="delivery-item">
            <h3 className="delivery-item__title">
              {t('delivery.consignment', {
                seq: confirmation.consignment.seq,
                origen: confirmation.consignment.origenDomicilio ?? t('common.none'),
                destino: confirmation.consignment.destinoDomicilio ?? t('common.none'),
              })}
            </h3>
            <div className="stack-sm small">
              <Datum
                label={t(confirmation.photoCount > 0 ? 'delivery.takenAt' : 'delivery.confirmedAt')}
                value={formatDateTime(confirmation.takenAt, i18n.language)}
                tabular
              />
              <Datum
                label={t('delivery.receivedAt')}
                value={formatDateTime(confirmation.receivedAt, i18n.language)}
                tabular
              />
              {administer ? (
                <>
                  <Datum label={t('delivery.nifConductor')} value={confirmation.nifConductor} tabular />
                  <Datum label={t('delivery.matricula')} value={confirmation.matricula} tabular />
                  <Datum label={t('delivery.phone')} value={confirmation.phone} />
                </>
              ) : null}
            </div>
            {administer ? (
              <DeliveryPhotos
                decaId={deca.id}
                confirmation={confirmation}
                onOpen={(n) => setOpen({ confirmation, n })}
              />
            ) : null}
          </li>
        ))}
      </ol>

      {open ? (
        <DeliveryPhotoDialog
          decaId={deca.id}
          documentNumber={deca.documentNumber}
          confirmation={open.confirmation}
          n={open.n}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </>
  )
}

function DeliveryPhotos({
  decaId,
  confirmation,
  onOpen,
}: {
  decaId: string
  confirmation: DeliveryConfirmation
  onOpen: (n: number) => void
}) {
  const { t } = useTranslation()
  const labelId = useId()
  const numbers = photoNumbers(confirmation)
  // A confirmation with no photograph (#179): said in words, and nothing to ask the server for.
  if (numbers.length === 0) return <p className="small muted">{t('delivery.noPhotos')}</p>
  return (
    <div className="stack-sm">
      <span className="datum__label" id={labelId}>
        {t('delivery.photos')}
      </span>
      <div className="delivery-photos" role="group" aria-labelledby={labelId}>
        {numbers.map((n) => (
          <DeliveryThumbnail
            key={n}
            decaId={decaId}
            confirmationId={confirmation.id}
            n={n}
            count={numbers.length}
            seq={confirmation.consignment.seq}
            onOpen={() => onOpen(n)}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * One photograph, small. Opens the dialog even while loading or failed: the dialog says which.
 * Named by its consignment, as the dialog's title is: a screen reader lists the buttons without the
 * heading above them, and two consignments' «1 de 1» would read the same.
 */
function DeliveryThumbnail({
  decaId,
  confirmationId,
  n,
  count,
  seq,
  onOpen,
}: {
  decaId: string
  confirmationId: string
  n: number
  count: number
  /** The consignment's number, as the confirmation recorded it. */
  seq: number
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const photo = useDeliveryPhoto(decaId, confirmationId, n)
  const url = useObjectUrl(photo.data)
  return (
    <button
      type="button"
      className="delivery-photo"
      aria-label={t('delivery.openPhoto', { n, count, seq })}
      title={photo.isError ? t('delivery.photoUnavailable') : undefined}
      onClick={onOpen}
    >
      {url ? (
        <img src={url} alt="" />
      ) : photo.isError ? (
        <Icon name="alert" size={20} />
      ) : (
        <span className="skeleton delivery-photo__placeholder" />
      )}
    </button>
  )
}

/**
 * One photograph full size, with «Descargar». The bytes are the thumbnail's, from the query cache:
 * opening and downloading read nothing again.
 */
function DeliveryPhotoDialog({
  decaId,
  documentNumber,
  confirmation,
  n,
  onClose,
}: {
  decaId: string
  documentNumber: string | null | undefined
  confirmation: DeliveryConfirmation
  n: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const photo = useDeliveryPhoto(decaId, confirmation.id, n)
  const url = useObjectUrl(photo.data)
  const title = t('delivery.photoTitle', {
    n,
    count: photoNumbers(confirmation).length,
    seq: confirmation.consignment.seq,
  })
  // Unique, since a consignment can be confirmed more than once, and in no language in particular,
  // as the PDF's own `DeCA-….pdf` is.
  const filename = `${documentNumber ?? 'DeCA'}-${confirmation.id.slice(0, 8)}-${n}.jpg`

  return (
    <Dialog
      open
      wide
      title={title}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          icon="download"
          disabled={!photo.data}
          onClick={() => {
            if (photo.data) saveBlob(photo.data, filename)
          }}
        >
          {t('common.download')}
        </Button>
      }
    >
      {url ? (
        <img className="delivery-photo-full" src={url} alt={title} />
      ) : photo.isError ? (
        <Notice tone="danger">{t('delivery.photoUnavailable')}</Notice>
      ) : (
        <LoadingRows rows={4} columns={1} />
      )}
    </Dialog>
  )
}
